import { getAddress, isAddress } from 'viem'
import type { VaultId } from '../types'
import { fromVaultId } from './vaultId'

export function toMarketId({
  marketRootAddress,
  marketProtocolIdentifier,
}: {
  marketRootAddress: string
  marketProtocolIdentifier: string
}) {
  if (isIdentifierEqual(marketRootAddress, marketProtocolIdentifier)) {
    return marketProtocolIdentifier
  }

  return `${marketRootAddress}-${marketProtocolIdentifier}`
}

export function toMarketAssetVaultId({
  marketRootAddress,
  marketProtocolIdentifier,
  assetAddress,
}: {
  marketRootAddress: string
  marketProtocolIdentifier: string
  assetAddress: string
}): VaultId {
  const marketId = toMarketId({ marketRootAddress: getAddress(marketRootAddress), marketProtocolIdentifier })
  const normalizedMarketId = isAddress(marketId) ? getAddress(marketId) : marketId

  return `${normalizedMarketId}-${getAddress(assetAddress)}` as VaultId
}

export function fromMarketAssetVaultId(vaultId: string): {
  marketRootAddress: string
  marketProtocolIdentifier: string
  assetAddress: string
} {
  const { address: marketRootAddress, uniqueDescriptor } = fromVaultId(vaultId)
  const assetSeparatorIndex = uniqueDescriptor.lastIndexOf('-')
  const marketProtocolIdentifier =
    assetSeparatorIndex === -1 ? marketRootAddress : uniqueDescriptor.slice(0, assetSeparatorIndex)
  const assetAddress = uniqueDescriptor.slice(assetSeparatorIndex + 1)

  if (!marketProtocolIdentifier || !isAddress(assetAddress)) {
    throw new Error(`Invalid market asset vault id: ${vaultId}`)
  }

  return {
    marketRootAddress,
    marketProtocolIdentifier: isAddress(marketProtocolIdentifier)
      ? getAddress(marketProtocolIdentifier)
      : marketProtocolIdentifier,
    assetAddress: getAddress(assetAddress),
  }
}

function isIdentifierEqual(left: string, right: string) {
  if (left === right) {
    return true
  }

  if (isAddress(left) && isAddress(right)) {
    return getAddress(left) === getAddress(right)
  }

  return false
}
